import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { Member, MemberFeeSummary, MemberService } from '../../../core/services/member.service';
import { computeMemberArrears, MemberArrears } from '../../../core/utils/member-arrears.util';

/** Consecutive unpaid/partial months (including the current one) before we call it "arrears". */
const ARREARS_THRESHOLD_MONTHS = 2;

interface CurrentPartialInfo {
  expected: number;
  paid: number;
  remaining: number;
}

type ArrearsInfo = MemberArrears;

/**
 * Self-service nudge for a logged-in member: highlights a partially-paid current
 * period, or multiple consecutive months of missed/partial dues, on the pages
 * a member actually looks at (dashboard, payments). No-ops entirely for admins.
 */
@Component({
  selector: 'app-member-dues-alert',
  standalone: true,
  imports: [CommonModule, RouterLink],
  template: `
    <ng-container *ngIf="kind() as k">
      <div [ngClass]="bannerClass()"
           class="rounded-lg border px-4 py-3 flex items-start gap-3 text-sm">
        <svg class="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <div class="flex-1">
          <div class="font-medium">{{ titleText() }}</div>
          <p class="mt-0.5 opacity-90">{{ bodyText() }}</p>
          <div class="flex flex-wrap gap-2 mt-2">
            @if (myMemberId(); as id) {
              <a [routerLink]="['/members', id]"
                 class="px-3 py-1 text-xs font-medium bg-white/80 rounded hover:bg-white">
                View my payments
              </a>
            }
          </div>
        </div>
      </div>
    </ng-container>
  `
})
export class MemberDuesAlertComponent implements OnInit {
  private auth = inject(AuthService);
  private memberService = inject(MemberService);

  readonly isAdmin = computed(() => this.auth.isSuperAdminSignal() || this.auth.isTenantAdminSignal());

  private readonly member = signal<Member | null>(null);
  private readonly feeSummary = signal<MemberFeeSummary | null>(null);

  readonly myMemberId = computed(() => {
    const m = this.member();
    return m ? (m.id ?? null) : null;
  });

  readonly currentPartial = computed<CurrentPartialInfo | null>(() => {
    const m = this.member();
    if (!m) return null;
    const expected = m.currentPeriodExpected ?? 0;
    const paid = m.currentPeriodTotalPaid ?? 0;
    if (expected > 0 && paid > 0 && paid < expected) {
      return { expected, paid, remaining: expected - paid };
    }
    return null;
  });

  readonly arrears = computed<ArrearsInfo | null>(() => {
    const m = this.member();
    if (!m) return null;
    const result = computeMemberArrears(m, this.feeSummary());
    return result.monthsBehind >= ARREARS_THRESHOLD_MONTHS ? result : null;
  });

  readonly kind = computed<'arrears' | 'partial' | null>(() => {
    if (this.isAdmin()) return null;
    if (this.arrears()) return 'arrears';
    if (this.currentPartial()) return 'partial';
    return null;
  });

  readonly bannerClass = computed(() => {
    switch (this.kind()) {
      case 'arrears': return 'bg-red-50 border-red-300 text-red-800';
      case 'partial': return 'bg-amber-50 border-amber-300 text-amber-800';
      default:        return '';
    }
  });

  readonly titleText = computed(() => {
    switch (this.kind()) {
      case 'arrears': return `You're behind on ${this.arrears()!.monthsBehind} months`;
      case 'partial': return 'Partial payment received';
      default:        return '';
    }
  });

  readonly bodyText = computed(() => {
    switch (this.kind()) {
      case 'arrears': {
        const a = this.arrears()!;
        return `Rs. ${a.totalOutstanding.toLocaleString()} is outstanding across the last ${a.monthsBehind} months. Please settle your membership dues as soon as possible.`;
      }
      case 'partial': {
        const p = this.currentPartial()!;
        return `You've paid Rs. ${p.paid.toLocaleString()} of Rs. ${p.expected.toLocaleString()} for this month. Rs. ${p.remaining.toLocaleString()} remaining — please complete your payment.`;
      }
      default:
        return '';
    }
  });

  ngOnInit(): void {
    if (this.isAdmin()) return;

    const now = new Date();
    this.memberService.getMembers({
      scope: 'self',
      page: 1,
      pageSize: 1,
      feeStatusMonth: now.getMonth() + 1,
      feeStatusYear: now.getFullYear()
    }).subscribe({
      next: (res) => {
        const m = res?.success ? res.data?.data?.[0] : null;
        const id = m?.id;
        if (!m || id == null) return;

        this.member.set(m);
        this.memberService.getMemberFeeSummary(id).subscribe({
          next: (fs) => {
            if (fs?.success && fs.data) this.feeSummary.set(fs.data);
          }
        });
      }
    });
  }
}
